/**
 * Find the index of the section containing currentBar (1-indexed bar position).
 * Returns -1 if no section contains the bar (e.g., gap between sections).
 *
 * Uses the same predicate as renderSections' is-active class to keep
 * render and tick paths consistent.
 *
 * @param {Array<{startBar,endBar}>} sections
 * @param {number} currentBar
 * @returns {number} index in sections array, or -1
 */
import { matchSemantic, nextCycleColor } from './section-colors.js';

export function getActiveSectionIndex(sections, currentBar) {
  if (!Array.isArray(sections) || !Number.isFinite(currentBar)) return -1;
  for (let i = 0; i < sections.length; i++) {
    const s = sections[i];
    const startClamp = Math.max(1, s.startBar);
    const endClamp   = Math.max(startClamp, s.endBar);
    if (currentBar >= startClamp && currentBar < endClamp) return i;
  }
  return -1;
}

function el(tag, cls, text) {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text != null) e.textContent = text;
  return e;
}

/**
 * Build read-only section ribbon DOM elements (positioned by bar grid).
 *
 * @param {Array<{id,name,startBar,endBar,color,loopMode}>} sections
 * @param {number} totalBars  Total bars in song duration.
 * @param {number} currentBar 1-indexed bar position of playhead (for active highlight).
 * @returns {HTMLDivElement[]} Array of <div class="rh-section [is-active]"> elements.
 */
export function renderSections(sections, totalBars, currentBar) {
  if (!Array.isArray(sections) || sections.length === 0) return [];
  if (!Number.isFinite(totalBars) || totalBars <= 0) return [];

  return sections.map((s, i) => {
    // Clamp to valid bar range. startBar is 1-indexed.
    const startClamp = Math.max(1, Math.min(totalBars + 1, s.startBar));
    const endClamp   = Math.max(startClamp, Math.min(totalBars + 1, s.endBar));
    const leftPct  = ((startClamp - 1) / totalBars) * 100;
    const widthPct = ((endClamp - startClamp) / totalBars) * 100;
    const isActive = currentBar >= startClamp && currentBar < endClamp;
    const looping = !!s.loopEnabled;

    let cls = 'rh-section';
    if (isActive) cls += ' is-active';
    if (looping)  cls += ' is-looping';
    const div = el('div', cls);
    div.style.left  = `${leftPct}%`;
    div.style.width = `${widthPct}%`;
    // Color resolution: explicit s.color → semantic by name → palette by index.
    // Desktop callers always pass s.color (set at mutation time), but the
    // fallback keeps the primitive safe for any consumer that omits it.
    const color = s.color || matchSemantic(s.name) || nextCycleColor(i);
    div.style.setProperty('--c', color);
    div.dataset.sectionId = s.id;
    div.dataset.startBar  = String(s.startBar);
    div.appendChild(el('span', 'rh-section-name', s.name));
    return div;
  });
}

/**
 * Render sections positioned by seconds (vs renderSections which positions by bars).
 * Used by web SPA which has startSec/endSec in manifest, not bar indices.
 *
 * @param {Array<{id,name,startSec,endSec,color}>} sections
 * @param {number} totalDurationSec
 * @param {number} currentSec  for active highlight
 * @returns {HTMLDivElement[]}
 */
export function renderSectionsBySec(sections, totalDurationSec, currentSec) {
  if (!Array.isArray(sections) || sections.length === 0) return [];
  if (!Number.isFinite(totalDurationSec) || totalDurationSec <= 0) return [];
  return sections.map((s, i) => {
    const div = document.createElement('div');
    div.className = 'rh-section';
    // `data-id` retained for backward-compat; `data-section-id` is the
    // canonical attribute used by section-handlers / loop-binding and
    // matches the desktop `renderSections` convention.
    div.dataset.id = s.id;
    div.dataset.sectionId = s.id;
    // Discoverability hint — rendered as a custom CSS tooltip via
    // `.rh-section[title]:hover::before` (see sections.css). Native title
    // attribute also acts as a fallback for screen readers / non-CSS hosts.
    div.title = 'Дабл-клик — залупить';
    const leftPct = (s.startSec / totalDurationSec) * 100;
    const widthPct = Math.max(0, ((s.endSec - s.startSec) / totalDurationSec) * 100);
    div.style.left = `${leftPct}%`;
    div.style.width = `${widthPct}%`;
    // Color resolution: explicit s.color → semantic match by name → cycle palette by index.
    // Mirrors desktop section-mutators which sets color at mutation time;
    // share manifests don't carry color, so we derive it at render time.
    // CSS var name must be --c to match desktop CSS (waves-column.css reads var(--c)).
    const color = s.color || matchSemantic(s.name) || nextCycleColor(i);
    div.style.setProperty('--c', color);
    if (Number.isFinite(currentSec) && currentSec >= s.startSec && currentSec < s.endSec) {
      div.classList.add('is-active');
    }
    // Nested .rh-section-name overlay (desktop CSS expects this child element
    // for the dark badge background + uppercase typography).
    const nameEl = document.createElement('div');
    nameEl.className = 'rh-section-name';
    nameEl.textContent = s.name || '';
    div.appendChild(nameEl);
    return div;
  });
}
